import { db, isFirebaseConfigured } from '../config/firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  runTransaction,
  serverTimestamp
} from 'firebase/firestore';

const generateOrderNumber = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n, len = 2) => String(n).padStart(len, '0');
  const ms = pad(d.getMilliseconds(), 3);
  const extra = pad(Math.floor(Math.random() * 1000), 3);
  return (
    String(d.getFullYear()) +
    pad(d.getMonth() + 1) +
    pad(d.getDate()) +
    pad(d.getHours()) +
    pad(d.getMinutes()) +
    pad(d.getSeconds()) +
    ms +
    extra
  );
};

const sortByCreatedAtDesc = (orders) => {
  const getMillis = (v) => {
    if (!v) return 0;
    if (typeof v?.toMillis === 'function') return v.toMillis();

    if (typeof v?.toDate === 'function') return v.toDate().getTime();
    const n = Date.parse(v);
    if (Number.isFinite(n)) return n;
    const asNum = Number(v);
    return Number.isFinite(asNum) ? asNum : 0;
  };

  return (Array.isArray(orders) ? orders : [])
    .slice()
    .sort((a, b) => getMillis(b?.createdAt) - getMillis(a?.createdAt));
};

// Create new order
export const createOrder = async (orderData) => {
  if (!isFirebaseConfigured || !db) throw new Error('Firebase not configured');

  try {
    const ordersRef = collection(db, 'orders');

    const normalizedEmail = String(orderData?.email || '').trim().toLowerCase();
    const orderNumber = generateOrderNumber();

    const order = {
      ...orderData,
      orderNumber,
      email: normalizedEmail,
      status: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(ordersRef, order);
    
    return {
      id: docRef.id,
      ...order
    };
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

export const createOrderAndDecrementStock = async (orderData) => {
  if (!isFirebaseConfigured || !db) throw new Error('Firebase not configured');

  const normalizedEmail = String(orderData?.email || '').trim().toLowerCase();
  const orderNumber = generateOrderNumber();

  const items = Array.isArray(orderData?.items) ? orderData.items : [];
  const required = items
    .map((it) => ({
      productId: it?.id,
      size: it?.size,
      color: it?.color,
      quantity: Number(it?.quantity || 0),
    }))
    .filter((x) => x.productId && x.size && x.color && Number.isFinite(x.quantity) && x.quantity > 0);

  if (required.length === 0) {
    throw new Error('Cart is empty.');
  }

  const order = {
    ...orderData,
    orderNumber,
    email: normalizedEmail,
    status: orderData?.status || 'pending',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const ordersRef = collection(db, 'orders');

  const result = await runTransaction(db, async (tx) => {
    const productCache = new Map();
    const updates = new Map();

    for (const r of required) {
      const pid = String(r.productId);
      const productRef = doc(db, 'products', pid);

      if (!productCache.has(pid)) {
        const snap = await tx.get(productRef);
        if (!snap.exists()) throw new Error(`Product not found: ${pid}`);
        productCache.set(pid, { ref: productRef, data: snap.data() });
      }

      const current = productCache.get(pid);
      const base = current?.data || {};
      const raw = base?.variantStock && typeof base.variantStock === 'object' ? base.variantStock : {};

      const key = `${String(r.color).toUpperCase()}|${String(r.size).toUpperCase()}`;
      const existingQty = Number(raw?.[key] || 0);
      if (!Number.isFinite(existingQty) || existingQty < r.quantity) {
        throw new Error(`Out of stock: ${pid} ${key}`);
      }

      const prev = updates.get(pid);
      const nextVariantStock = {
        ...(prev?.variantStock || raw),
        [key]: existingQty - r.quantity,
      };

      updates.set(pid, { variantStock: nextVariantStock });
    }

    for (const [pid, update] of updates.entries()) {
      const current = productCache.get(pid);
      const ref = current.ref;
      const nextVariantStock = update.variantStock;
      const total = Object.values(nextVariantStock).reduce((sum, v) => sum + (Number(v) || 0), 0);
      tx.update(ref, {
        variantStock: nextVariantStock,
        stockQuantity: Number.isFinite(Number(total)) ? Number(total) : 0,
        inStock: Number(total) > 0,
        updatedAt: serverTimestamp(),
      });
    }

    const created = await addDoc(ordersRef, order);
    return { id: created.id };
  });

  return { id: result?.id, ...order };
};

// Get user orders
export const getUserOrders = async (userId) => {
  if (!isFirebaseConfigured || !db) throw new Error('Firebase not configured');

  try {
    const ordersRef = collection(db, 'orders');
    try {
      const q = query(ordersRef, where('userId', '==', userId), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      const msg = String(error?.message || '').toLowerCase();
      const code = String(error?.code || '').toLowerCase();
      const isIndexIssue = code.includes('failed-precondition') || msg.includes('index');
      if (!isIndexIssue) throw error;

      const q = query(ordersRef, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      const rows = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      return sortByCreatedAtDesc(rows);
    }
  } catch (error) {
    console.error('Error fetching orders:', error);
    return [];
  }
};

// Get order by ID
export const getOrderById = async (orderId) => {
  if (!isFirebaseConfigured || !db) throw new Error('Firebase not configured');

  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    
    if (orderSnap.exists()) {
      return {
        id: orderSnap.id,
        ...orderSnap.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching order:', error);
    return null;
  }
};

// Get all orders for admin
export const getAllOrdersAdmin = async () => {
  if (!isFirebaseConfigured || !db) throw new Error('Firebase not configured');

  try {
    const ordersRef = collection(db, 'orders');
    const q = query(ordersRef, orderBy('createdAt', 'desc'));
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching orders:', error);
    return [];
  }
};

// Update order by ID
export const updateOrderById = async (orderId, patch) => {
  if (!orderId) throw new Error('Missing order id');

  if (!isFirebaseConfigured || !db) throw new Error('Firebase not configured');

  await updateDoc(doc(db, 'orders', orderId), { ...(patch || {}), updatedAt: serverTimestamp() });
  return { id: orderId, ...(patch || {}) };
};

export const getOrdersByEmail = async (email) => {
  const needle = String(email || '').trim().toLowerCase();
  if (!needle) return [];

  if (!isFirebaseConfigured || !db) throw new Error('Firebase not configured');

  try {
    const ordersRef = collection(db, 'orders');
    try {
      const q = query(ordersRef, where('email', '==', needle), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      const msg = String(error?.message || '').toLowerCase();
      const code = String(error?.code || '').toLowerCase();
      const isIndexIssue = code.includes('failed-precondition') || msg.includes('index');
      if (!isIndexIssue) throw error;

      const q = query(ordersRef, where('email', '==', needle));
      const querySnapshot = await getDocs(q);
      const rows = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      return sortByCreatedAtDesc(rows);
    }
  } catch (error) {
    console.error('Error fetching orders by email:', error);
    return [];
  }
};